import logger from '@/utils/logger';
/**
 * M'EyeBus - Marker Icons Helper
 *
 * Gestion centralisée des icônes de marqueurs pour les cartes Mapbox
 */

// Chemins des icônes de marqueurs
const MARKER_PATHS = {
  bus: {
    active: '/markers/bus-active.png',
    stopped: '/markers/bus-stopped.png',
    inactive: '/markers/bus-inactive.png',
    delayed: '/markers/bus-delayed.png',
    maintenance: '/markers/bus-maintenance.png',
  },
  stop: '/markers/bus-stop.png',
  school: '/markers/school.png',
  currentPosition: '/markers/current-position.png',
  student: '/markers/student.png',
};

// Fallback vers des icônes SVG si les PNG ne sont pas disponibles
const SVG_FALLBACKS = {
  bus: (color = '#10B981', size = 24) =>
    `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
    <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <!-- Ombre portée -->
      <rect x="6" y="2" width="12" height="20" rx="2" fill="rgba(0,0,0,0.3)" transform="translate(0, 1)"/>
      <!-- Corps du bus (Vue de dessus stricte, pointant vers le HAUT/NORD) -->
      <rect x="6" y="2" width="12" height="20" rx="2" fill="${color}"/>
      <!-- Pare-brise avant (en haut) -->
      <rect x="7" y="3" width="10" height="3" rx="1" fill="#1E293B"/>
      <!-- Pare-brise arrière (en bas) -->
      <rect x="7" y="19" width="10" height="2" rx="0.5" fill="#1E293B"/>
      <!-- Toit (plus clair) -->
      <rect x="7" y="7" width="10" height="11" rx="1" fill="rgba(255,255,255,0.2)"/>
      <!-- Climatisation sur le toit -->
      <rect x="9" y="14" width="6" height="3" rx="0.5" fill="rgba(255,255,255,0.4)"/>
      <!-- Flèche directionnelle claire pointant vers le HAUT (Nord) -->
      <path d="M12 8L15 12H9L12 8Z" fill="white" opacity="0.9"/>
    </svg>
  `)}`,

  stop: (color = '#8B5CF6', size = 16) =>
    `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
    <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="${color}" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="8" stroke="white" stroke-width="2"/>
    </svg>
  `)}`,

  school: (color = '#1E40AF', size = 32) => {
    const svg = `<svg width="${size}" height="${size}" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="4" y="6" width="24" height="24" rx="6" fill="rgba(0,0,0,0.2)"/>
      <rect x="4" y="4" width="24" height="24" rx="6" fill="${color}" stroke="white" stroke-width="2"/>
      <path d="M16 10L9 13.5L16 17L21.5 14.25V19.5C21.5 19.78 21.72 20 22 20C22.28 20 22.5 19.78 22.5 19.5V13.75L23 13.5L16 10Z" fill="white"/>
      <path d="M10.5 16.25V19.5C10.5 21.16 12.96 22.5 16 22.5C19.04 22.5 21.5 21.16 21.5 19.5V16.25C20.45 17.31 18.37 18 16 18C13.63 18 11.55 17.31 10.5 16.25Z" fill="white"/>
    </svg>`;
    // Nettoyer les retours à la ligne et les espaces multiples avant d'encoder
    const cleanSvg = svg.replace(/\n/g, '').replace(/\s+/g, ' ').trim();
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(cleanSvg)}`;
  },

  currentPosition: (color = '#EF4444', size = 20) =>
    `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
    <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="${color}" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="8" fill="${color}"/>
      <circle cx="12" cy="12" r="4" fill="white"/>
    </svg>
  `)}`,

  student: (color = '#F97316', size = 20) =>
    `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`
    <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="${color}" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
    </svg>
  `)}`,
};

// Couleurs par statut de bus — alignées avec STATUS_COLORS de FleetMap.jsx
const BUS_COLORS = {
  active: '#22c55e', // vert  – en trajet, en mouvement
  stopped: '#eab308', // jaune – en trajet, à l'arrêt
  parked: '#9ca3af', // gris  – moteur coupé (ignition=false)
  offline: '#ef4444', // rouge – hors ligne
  inactive: '#64748b', // gris-bleu – statut inconnu
  delayed: '#ef4444', // rouge
  maintenance: '#ef4444', // rouge
};

/**
 * Obtenir l'icône d'un marqueur
 * @param {string} type - Type de marqueur ('bus', 'stop', 'school', 'currentPosition', 'student')
 * @param {string} status - Statut (pour les bus: 'active', 'stopped', 'inactive', 'delayed', 'maintenance')
 * @param {boolean} useFallback - Utiliser le fallback SVG si l'image n'existe pas
 * @returns {string} URL de l'icône
 */
export const getMarkerIcon = (type, status = null, useFallback = true) => {
  if (type === 'bus' && status) {
    // FORCE l'utilisation du SVG "vue de dessus" pour que la rotation fonctionne correctement
    // On ignore les images PNG 3D existantes
    const color = BUS_COLORS[status] || BUS_COLORS.inactive;
    return SVG_FALLBACKS.bus(color);
  }

  if (type === 'school') {
    // FORCE l'utilisation du SVG pour l'école car le PNG a été supprimé
    return SVG_FALLBACKS.school();
  }

  const path = MARKER_PATHS[type];
  if (path) return path;

  // Fallback SVG
  if (useFallback && SVG_FALLBACKS[type]) {
    return SVG_FALLBACKS[type]();
  }

  // Fallback ultime
  return SVG_FALLBACKS.bus('#9CA3AF');
};

/**
 * Obtenir la couleur d'un statut de bus
 * @param {string} status - Statut du bus
 * @returns {string} Code couleur hexadécimal
 */
export const getBusStatusColor = (status) => {
  return BUS_COLORS[status] || BUS_COLORS.inactive;
};

/**
 * Vérifier si une icône existe
 * @param {string} type - Type de marqueur
 * @param {string} status - Statut (optionnel)
 * @returns {Promise<boolean>} True si l'icône existe
 */
export const checkMarkerExists = async (type, status = null) => {
  const iconPath = getMarkerIcon(type, status, false);
  if (!iconPath) return false;

  try {
    const response = await fetch(iconPath, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
};

/**
 * Précharger toutes les icônes de marqueurs
 * @returns {Promise<void>}
 */
export const preloadMarkerIcons = async () => {
  const iconsToPreload = [
    ...Object.values(MARKER_PATHS.bus),
    MARKER_PATHS.stop,
    MARKER_PATHS.school,
    MARKER_PATHS.currentPosition,
    MARKER_PATHS.student,
  ];

  const promises = iconsToPreload.map((path) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = resolve;
      img.onerror = resolve; // Continue même en cas d'erreur
      img.src = path;
    });
  });

  await Promise.all(promises);
  logger.debug('✅ [MarkerIcons] Toutes les icônes préchargées');
};

/**
 * Créer une icône Mapbox GL à partir d'un marqueur
 * @param {string} type - Type de marqueur
 * @param {string} status - Statut (optionnel)
 * @param {number} size - Taille de l'icône
 * @returns {object} Configuration d'icône pour Mapbox GL
 */
export const createMapboxIcon = (type, status = null, size = 1) => {
  return {
    'icon-image': getMarkerIcon(type, status),
    'icon-size': size,
    'icon-allow-overlap': true,
  };
};

export default {
  getMarkerIcon,
  getBusStatusColor,
  checkMarkerExists,
  preloadMarkerIcons,
  createMapboxIcon,
  MARKER_PATHS,
  BUS_COLORS,
};
